import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { HedgePosition, DerivedHedgePosition } from '@sp-types/positions';
import { ExposureHorizonWindow } from '../../../utils/exposureHorizon';
import { useCategoryTerms } from '../../../utils/categoryTerms';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';
import { useBaseCurrency } from '../../../hooks/useBaseCurrency';
import { useFx } from '../../../hooks/useFx';
import {
  getQuantity,
  isOutOfScope as checkPositionOutOfScope,
  groupByFormula,
} from '../../../utils/positionUtils';
import { PositionsGrid, GroupByConfig } from '../../../components/PositionsGrid';
import { getCurrencySymbol, formatCurrency, formatDate, formatQuantity } from '../../../components/PositionsGrid/gridUtils';

type HedgePositionEntry = HedgePosition | DerivedHedgePosition;

interface HedgesGridProps {
  positions: HedgePositionEntry[];
  formulaColor: string;
  baseCurrency: string;
  isGeneralMode?: boolean;
  formulaName?: string;
  formulaId?: string;
  exposureWindow?: ExposureHorizonWindow;
}

export const HedgesGrid: React.FC<HedgesGridProps> = ({
  positions,
  formulaColor,
  baseCurrency,
  isGeneralMode = true,
  formulaName,
  formulaId,
  exposureWindow,
}) => {
  const { hedge: hedgeTerm } = useCategoryTerms();
  const { materialsMap } = useMaterialsStore();
  // Use new API-backed spot prices (not legacy useCurrencyRates)
  const { data: formulasPricesData } = useFormulasPrices();
  const pricesMap: Record<string, number> = React.useMemo(() => {
    if (!formulasPricesData?.pricesMap) return {};
    return Object.fromEntries(
      Object.entries(formulasPricesData.pricesMap).map(([key, val]) => [key, val.price])
    );
  }, [formulasPricesData?.pricesMap]);
  const baseCurr = useBaseCurrency();
  const { convertToBase } = useFx(baseCurr);

  // ── Row mapper ────────────────────────────────────────────
  const positionToRow = React.useCallback(
    (p: HedgePositionEntry, idx: number) => {
      const qty = getQuantity(p);
      const absQty = Math.abs(qty);
      const tradePrice = p.underlying_price || null;
      const hedgeCurrency = p.hedge_currency || p.contract_currency || '';
      const spotPrice = pricesMap[p.formula_code] || 0;
      // Spot value: always use spot price for consistent exposure valuation
      const spotValue = qty * spotPrice;
      // Trade value: convert to base currency for consistent display
      let tradeValue: number | null = null;
      if (tradePrice) {
        const { amountBase } = hedgeCurrency && hedgeCurrency.toUpperCase() !== baseCurr.toUpperCase()
          ? convertToBase(qty * tradePrice, hedgeCurrency)
          : { amountBase: qty * tradePrice };
        tradeValue = amountBase;
      }

      // Handle both DerivedHedgePosition and raw HedgePosition fields
      const derived = p as DerivedHedgePosition;
      const isDerived = derived.derived_from_base === true;

      const baseCode = isDerived
        ? derived.base_formula_code || ''
        : p.underlying_formula_code || '';

      const allocPct = isDerived
        ? (derived.allocation_percentage != null ? derived.allocation_percentage * 100 : null)
        : p.allocation_percent;

      const allocMt = isDerived
        ? (derived.original_quantity ?? null)
        : p.allocated_underlying_mt;

      const priceRatio = isDerived
        ? (derived.original_quantity && derived.hedged_quantity_mt
          ? derived.original_quantity / derived.hedged_quantity_mt
          : null)
        : p.conversion_ratio;

      return {
        id: `${p.hedge_position_id}-${idx}`,
        formula_code: p.formula_code,
        formula_name: materialsMap[p.formula_code]?.name || p.formula_name || p.formula_code,
        statement_date: p.statement_date || '',
        instrument_type: p.instrument_type,
        option_type: p.option_type || null,
        hedge_direction: qty >= 0 ? 'Buy' : 'Sell',
        maturity_date: p.maturity_date,
        base_material: baseCode ? (materialsMap[baseCode]?.name || baseCode) : '',
        allocation_percent: allocPct,
        terminal_label: isDerived ? (derived as any).terminal_label || null : null,
        allocated_mt: allocMt,
        conversion_ratio: priceRatio,
        trade_price: tradePrice,
        hedge_currency: hedgeCurrency,
        quantity: absQty,
        spot_value: spotValue,
        trade_value: tradeValue,
        broker_name: p.broker_name || '',
        exchange_code: p.exchange_code || '',
        contracts_count: p.contracts_count || null,
        isOutOfScope: exposureWindow ? checkPositionOutOfScope(p, exposureWindow) : false,
      };
    },
    [pricesMap, materialsMap, exposureWindow, convertToBase],
  );

  // ── Summary row builder ─────────────────────────────────
  const buildSummaryRow = React.useCallback(
    (allRows: Record<string, any>[]) => {
      const totalQty = allRows.reduce((s, r) => s + (r.quantity || 0), 0);
      const totalVal = allRows.reduce((s, r) => s + (r.spot_value || 0), 0);
      const totalTradeVal = allRows.reduce((s, r) => s + (r.trade_value || 0), 0);
      const totalAllocated = allRows.reduce((s, r) => s + (r.allocated_mt || 0), 0);

      // Show weighted avg trade price when there are priced rows
      const pricedRows = allRows.filter(r => r.trade_price > 0);
      const pricedQty = pricedRows.reduce((s, r) => s + (r.quantity || 0), 0);
      const pricedTradeVal = pricedRows.reduce((s, r) => s + (r.trade_value || 0), 0);

      return {
        id: '__summary__',
        formula_code: '',
        formula_name: 'Total',
        statement_date: '',
        instrument_type: '',
        hedge_direction: '',
        maturity_date: '',
        base_material: '',
        allocation_percent: null,
        allocated_mt: totalAllocated || null,
        conversion_ratio: null,
        trade_price: totalQty > 0 && pricedQty > 0 ? pricedTradeVal / pricedQty : null,
        hedge_currency: totalQty > 0 && pricedQty > 0 ? baseCurr : '',
        quantity: totalQty,
        spot_value: totalVal,
        trade_value: totalTradeVal || null,
        broker_name: '',
        exchange_code: '',
        contracts_count: null,
        isSummary: true,
      };
    },
    [baseCurr],
  );

  // ── Banner summary helper ─────────────────────────────────
  const buildSummary = React.useCallback(
    (children: Record<string, any>[]) => {
      const totalQty = children.reduce((s, r) => s + (r.quantity || 0), 0);
      const totalVal = children.reduce((s, r) => s + (r.spot_value || 0), 0);
      return `${formatQuantity(totalQty)} MT · ${getCurrencySymbol(baseCurr)}${formatCurrency(Math.abs(totalVal))} ${baseCurr}`;
    },
    [baseCurr],
  );

  // ── Group configurations ──────────────────────────────────
  const groupConfigs: GroupByConfig[] = useMemo(() => [
    {
      key: 'Formula',
      modes: ['general'],
      isDefaultFor: 'general',
      getGroupKey: (row) => row.formula_code,
      getGroupLabel: (key) => materialsMap[key]?.name || key,
      getGroupSummary: (_key: string, children: Record<string, any>[]) => buildSummary(children),
      getGroupColor: (key: string) => ((useAccountStore.getState() as any).formulaColorDictionary?.[key] || '#94a3b8'),
    },
    {
      key: 'Month',
      modes: ['general', 'formula'],
      isDefaultFor: 'formula',
      getGroupKey: (row) => row.maturity_date?.substring(0, 7) || 'Unknown',
      getGroupLabel: (key) => {
        const [y, m] = key.split('-');
        return new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      },
      getGroupSummary: (_key: string, children: Record<string, any>[]) => buildSummary(children),
    },
    {
      key: 'None',
      modes: ['formula'],
    },
  ], [materialsMap, buildSummary]);

  // ── Column definitions ────────────────────────────────────
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'formula_name',
      headerName: 'Formula',
      flex: 1,
      minWidth: 130,
      renderCell: (params: any) => <div className="text-slate-700">{params.value}</div>,
    },
    {
      field: 'statement_date',
      headerName: 'Created',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params: any) => <div className="text-slate-600">{formatDate(params.value)}</div>,
    },
    {
      field: 'maturity_date',
      headerName: 'Maturity',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params: any) => <div className="text-slate-600">{formatDate(params.value)}</div>,
    },
    {
      field: 'instrument_type',
      headerName: 'Instrument',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params: any) => {
        const val = params.value as string;
        if (val === 'option') {
          const optType = params.row.option_type as string | null;
          const label = optType ? optType.toUpperCase() : 'OPTION';
          return (
            <span
              className="text-xs font-medium px-1.5 py-0.5 rounded"
              style={{ background: '#EDE9FE', color: '#6D28D9' }}
            >
              {label}
            </span>
          );
        }
        return <div className="text-slate-600 text-xs">{val}</div>;
      },
    },
    {
      field: 'hedge_direction',
      headerName: 'Direction',
      flex: 0.4,
      minWidth: 70,
      renderCell: (params: any) => {
        if (!params.value) return null;
        const isBuy = params.value === 'Buy';
        const color = isBuy ? '#16a34a' : '#dc2626';
        const Icon = isBuy ? ArrowUpward : ArrowDownward;
        return (
          <Tooltip title={isBuy ? 'Buy hedge (long exposure)' : 'Sell hedge (short exposure)'} arrow>
            <div className="flex items-center gap-1">
              <Icon sx={{ fontSize: 16, color }} />
              <span className="text-slate-700">{params.value}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'base_material',
      headerName: 'Base Material',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params: any) => <div className="text-slate-600 text-xs">{params.value}</div>,
    },
    {
      field: 'allocation_percent',
      headerName: 'Alloc %',
      flex: 0.45,
      minWidth: 75,
      renderCell: (params: any) => {
        const v = params.value;
        if (v == null) return <div />;
        return <div className="text-slate-600">{Number(v).toFixed(1)}%</div>;
      },
    },
    {
      field: 'allocated_mt',
      headerName: 'Allocated (MT)',
      flex: 0.5,
      minWidth: 90,
      renderCell: (params: any) => {
        const v = params.value;
        return v != null ? <div className="text-slate-700">{formatQuantity(Number(v))}</div> : <div />;
      },
    },
    {
      field: 'conversion_ratio',
      headerName: 'Price Ratio',
      flex: 0.35,
      minWidth: 60,
      renderCell: (params: any) => {
        const v = params.value;
        return v != null ? <div className="text-slate-600">{Number(v).toFixed(2)}</div> : <div />;
      },
    },
    {
      field: 'trade_price',
      headerName: 'Trade Price',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params: any) => {
        const price = params.value;
        const isAgg = params.row.isSummary || params.row._isGroupRow;
        if (!price) return <div className="text-slate-300 text-[11px] italic">Not provided</div>;
        const cur = params.row.hedge_currency;
        const isDifferentCurrency = !isAgg && cur && cur.toUpperCase() !== baseCurr.toUpperCase();
        const { amountBase } = isDifferentCurrency ? convertToBase(price, cur) : { amountBase: price };
        return (
          <Tooltip title={isAgg ? 'Weighted average trade price' : ''} arrow enterDelay={300}>
            <div className="flex flex-col leading-tight">
              <span className="text-slate-700 flex items-center gap-1">
                {getCurrencySymbol(cur)}{Number(price).toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-xs text-slate-400">{cur}</span>
                {isAgg && <span className="text-[10px] font-medium text-slate-300 bg-slate-50 border border-slate-200 rounded px-1 leading-tight">avg</span>}
              </span>
              {isDifferentCurrency && amountBase === null && (
                <span className="text-[11px] text-slate-300 italic">Converting…</span>
              )}
              {isDifferentCurrency && amountBase !== null && amountBase > 0 && (
                <span className="text-[10px] text-slate-400/70 italic">≈ {getCurrencySymbol(baseCurr)}{amountBase.toLocaleString(undefined, { maximumFractionDigits: 0 })} {baseCurr}</span>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'quantity',
      headerName: 'Quantity (MT)',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params: any) => <div className="text-slate-700">{formatQuantity(Number(params.value))}</div>,
    },
    {
      field: 'spot_value',
      headerName: `Spot Value (${baseCurr})`,
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: any) => {
        const v = Number(params.value);
        return v !== 0 ? (
          <div className="text-slate-700">
            {getCurrencySymbol(baseCurr)}{formatCurrency(v)} <span className="text-xs text-slate-400">{baseCurr}</span>
          </div>
        ) : <div className="text-slate-300 text-[11px] italic">Not provided</div>;
      },
    },
    {
      field: 'trade_value',
      headerName: `Trade Value`,
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: any) => {
        const v = params.value;
        if (v === null) return <div className="text-slate-300 text-[11px] italic">Converting…</div>;
        if (v == null) return <div className="text-slate-300 text-[11px] italic">Not provided</div>;
        const num = Number(v);
        return num !== 0 ? (
          <div className="text-slate-600 text-xs">
            {getCurrencySymbol(baseCurr)}{formatCurrency(num)} <span className="text-xs text-slate-400">{baseCurr}</span>
          </div>
        ) : <div className="text-slate-300 text-[11px] italic">Not provided</div>;
      },
    },
  ], [baseCurr]);

  const formulaCount = isGeneralMode ? Object.keys(groupByFormula(positions)).length : undefined;

  // ── Render ────────────────────────────────────────────────
  return (
    <PositionsGrid
      positions={positions}
      positionToRow={positionToRow}
      columns={columns}
      groupConfigs={groupConfigs}
      buildSummaryRow={buildSummaryRow}
      categoryTerm={hedgeTerm}
      isGeneralMode={isGeneralMode}
      formulaId={formulaId}
      formulaName={formulaName}
      formulaColor={formulaColor}
      uniqueFormulas={formulaCount}
    />
  );
};
